/**
 * Live report preview: a real auto-layout table so columns fit their content
 * width automatically. Styling (header/row colors, banding, wrap) comes from
 * the draft ReportConfig, and totals/grouping mirror the exported artifacts.
 */
import { ArrowDown, ArrowUp } from 'lucide-react'
import clsx from 'clsx'
import { useCanEditModule } from '@/auth/useCanEditModule'
import { useClientProfile } from '@/hooks/useClientProfile'
import { safeBrandingPath } from '@/utils/brandingPath'
import type { Loan } from '@/types'
import { COLUMN_MAP } from './columns'
import { loanExceptions } from './helpers'
import { aggregate, formatAggValue, groupLoans } from './reporting'
import type { AggMethod, ColumnId, ReportConfig } from './types'
import { PipelineTableCell } from './PipelineTableCell'

const WRAP_MAX_WIDTH = 280

export function ReportPreview({
  loans,
  columns,
  config,
  onSort,
  reportName = 'Pipeline Report',
  editableResponsibleParty = false,
}: {
  loans: Loan[]
  columns: ColumnId[]
  config: ReportConfig
  onSort: (id: ColumnId) => void
  /** Fallback when the report title field is empty — usually the saved-view name. */
  reportName?: string
  /** The live Studio may edit loan assignments; document previews stay read-only. */
  editableResponsibleParty?: boolean
}) {
  const canEditPipeline = useCanEditModule('pipeline')
  const canAssignResponsibleParty = editableResponsibleParty && canEditPipeline
  // The client's own mark, as the export carries it: from the client profile.
  const client = useClientProfile()
  // Only a same-origin /branding file is ever loaded as the mark.
  const clientLogo = safeBrandingPath(client.logoDark)
  const defs = columns.map((id) => COLUMN_MAP.get(id)).filter((def) => def != null)
  const style = config.style
  const groups = groupLoans(loans, config.groupBy)
  const groupColumn = config.groupBy ? COLUMN_MAP.get(config.groupBy) : null
  const title = config.title.trim() || reportName.trim() || 'Pipeline Report'
  const subtitle = config.subtitle.trim()

  const cellClass = () =>
    clsx(
      'border-b border-black/20 px-2 py-1.5 align-top text-center text-[11px]',
      style.wrapText ? 'whitespace-normal break-words' : 'whitespace-nowrap',
    )

  const totalsRow = (rows: Loan[], label: string, grand: boolean) => (
    <tr>
      {defs.map((def, index) => {
        const method: AggMethod = config.totals[def.id] ?? 'none'
        const value = method === 'none' ? null : aggregate(rows, def.id, method)
        return (
          <td
            key={def.id}
            className={clsx(cellClass(), 'font-mono text-[10px]', grand ? 'font-semibold' : '')}
            style={{
              backgroundColor: style.headerBg,
              color: style.headerText,
              borderTop: `1px solid ${style.headerText}55`,
            }}
            title={method === 'none' ? undefined : `${method} of ${def.label}`}
          >
            {method === 'none'
              ? index === 0
                ? label
                : ''
              : formatAggValue(value, def.id, method)}
          </td>
        )
      })}
    </tr>
  )

  return (
    <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-white/[0.06]" style={{ backgroundColor: style.rowBg }}>
      <div
        className="flex items-start justify-between gap-4 border-b border-black/10 px-4 py-3"
        style={{ backgroundColor: style.rowBg }}
      >
        <div className="min-w-0 flex-1">
          <div className="truncate text-[16px] font-semibold tracking-tight" style={{ color: style.rowText }}>
            {title}
          </div>
          {subtitle ? (
            <div className="mt-0.5 truncate text-[11px] opacity-70" style={{ color: style.rowText }}>
              {subtitle}
            </div>
          ) : null}
        </div>
        {clientLogo ? (
          <img
            src={clientLogo}
            alt={client.name}
            className="h-9 w-auto shrink-0 object-contain"
          />
        ) : null}
      </div>
      {loans.length === 0 ? (
        <div className="flex h-40 items-center justify-center text-sm" style={{ color: style.rowText }}>
          No deals match the report filters.
        </div>
      ) : (
        <table className="w-full table-auto border-collapse">
          <thead className="sticky top-0 z-10">
            <tr>
              {defs.map((def) => {
                const active = config.sortId === def.id
                return (
                  <th
                    key={def.id}
                    className="cursor-pointer select-none whitespace-nowrap px-2 py-2 text-center text-[9px] font-medium uppercase tracking-[0.1em]"
                    style={{ backgroundColor: style.headerBg, color: style.headerText }}
                    onClick={() => onSort(def.id)}
                    title={`Sort by ${def.label}`}
                  >
                    <span className="inline-flex items-center justify-center gap-1">
                      {def.label}
                      {active ? (
                        config.sortDir === 'asc' ? (
                          <ArrowUp className="h-2.5 w-2.5" />
                        ) : (
                          <ArrowDown className="h-2.5 w-2.5" />
                        )
                      ) : null}
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {groups.map((group, groupIndex) => {
              let zebra = 0
              return (
                <FragmentRows
                  key={config.groupBy ? group.key : `all-${groupIndex}`}
                  header={
                    config.groupBy && groupColumn ? (
                      <tr>
                        <td
                          colSpan={defs.length}
                          className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.1em]"
                          style={{
                            backgroundColor: style.rowAltBg,
                            color: style.rowText,
                            borderTop: `1px solid ${style.headerText}33`,
                          }}
                        >
                          {groupColumn.label}: {group.key} · {group.rows.length} deal{group.rows.length === 1 ? '' : 's'}
                        </td>
                      </tr>
                    ) : null
                  }
                  rows={group.rows.map((loan) => {
                    const banded = style.zebra && zebra++ % 2 === 1
                    const flagged = style.highlightExceptions && loanExceptions(loan).length > 0
                    return (
                      <tr
                        key={loan.id}
                        style={{
                          backgroundColor: banded ? style.rowAltBg : style.rowBg,
                          ...(flagged
                            ? { backgroundImage: 'linear-gradient(rgba(248,113,113,0.10), rgba(248,113,113,0.10))' }
                            : {}),
                        }}
                      >
                        {defs.map((def) => (
                          <td
                            key={def.id}
                            className={clsx(
                              cellClass(),
                              (def.kind === 'number' || def.kind === 'date') && 'font-mono',
                            )}
                            style={{
                              color: style.rowText,
                              borderLeft:
                                flagged && def.id === defs[0]?.id ? '3px solid rgba(248,113,113,0.7)' : undefined,
                              maxWidth: style.wrapText && def.kind === 'text' ? WRAP_MAX_WIDTH : undefined,
                            }}
                          >
                            <PipelineTableCell
                              column={def}
                              loan={loan}
                              editableResponsibleParty={canAssignResponsibleParty}
                              presentation="report"
                              selectStyle={{
                                backgroundColor: banded ? style.rowAltBg : style.rowBg,
                                color: style.rowText,
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    )
                  })}
                  footer={config.groupBy && config.showTotals ? totalsRow(group.rows, 'Subtotal', false) : null}
                />
              )
            })}
            {config.showTotals ? totalsRow(loans, 'Total', true) : null}
          </tbody>
        </table>
      )}
    </div>
  )
}

/** Keyed fragment helper so grouped sections stay well-typed inside <tbody>. */
function FragmentRows({
  header,
  rows,
  footer,
}: {
  header: React.ReactNode
  rows: React.ReactNode
  footer: React.ReactNode
}) {
  return (
    <>
      {header}
      {rows}
      {footer}
    </>
  )
}
